import { useEffect, useRef, useState } from "react";

/**
 * Physical mouse capture for the visualizer. A real mouse button or wheel tick over the
 * VISUALIZER RENDER AREA (the board + mouse map, `.kb-wrap`) reflects on the on-screen mouse - the
 * press half of "see your setup reflected" - the same broad coverage the keyboard has. A button is
 * HELD (its zone stays depressed until release); the wheel is a momentary flash. `e.button` maps to
 * zone codes: 0 left, 1 middle, 2 right, 3 back (Mouse4 / XBUTTON1), 4 forward (Mouse5 /
 * XBUTTON2); `deltaY` to WheelUp / WheelDown.
 *
 * Captured at the window via `mousedown`/`mouseup`/`wheel` (mouse events carry every button and let
 * us `preventDefault` the OS action - context menu, back/forward, middle autoscroll - over the area).
 * The LEFT button over a KEYCAP is the one exception: it stays the cap click-to-inspect (you cannot
 * press a foreign board's keys), so left is captured everywhere in the area except on a `.kc`.
 *
 * Returns the set of currently-held zone codes; the page unions it into `pressedCodes`. Known
 * limitation: if the mouse driver maps the side buttons to "browser back/forward"
 * (a navigation command, not a button event), the page never receives a `mousedown` for them and
 * cannot capture them - rebind them to raw "button 4/5" in the mouse software, or it is unfixable here.
 */
const BUTTON_ZONE: Record<number, string> = {
  0: "MouseLeft",
  1: "MouseMiddle",
  2: "MouseRight",
  3: "Mouse4",
  4: "Mouse5",
};

const WHEEL_GATE_MS = 160;

/** True when the event happened over the visualizer render area (board + mouse map). */
function inArea(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(".kb-wrap") != null;
}

export function useMouseButtons(
  press: (code: string) => void,
  flash: (codes: string[]) => void,
  enabled: boolean,
): ReadonlySet<string> {
  const [held, setHeld] = useState<ReadonlySet<string>>(() => new Set());
  const pressRef = useRef(press);
  pressRef.current = press;
  const flashRef = useRef(flash);
  flashRef.current = flash;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const wheelGate = useRef(0);

  useEffect(() => {
    // the zone a button-press maps to over the render area, or null to leave the event alone
    const zoneFor = (e: MouseEvent): string | null => {
      const t = e.target;
      if (!(t instanceof Element) || t.closest(".kb-wrap") == null) return null;
      const zone = BUTTON_ZONE[e.button];
      if (!zone) return null;
      // left over a keycap stays the cap click-to-inspect; everywhere else in the area it is MouseLeft
      if (e.button === 0 && t.closest(".kc") != null) return null;
      return zone;
    };
    const down = (e: MouseEvent) => {
      if (!enabledRef.current) return;
      const zone = zoneFor(e);
      if (!zone) return;
      e.preventDefault(); // the physical button is reflected on the board, not handed to the OS
      setHeld((prev) => (prev.has(zone) ? prev : new Set(prev).add(zone)));
      pressRef.current(zone);
    };
    const up = (e: MouseEvent) => {
      const zone = BUTTON_ZONE[e.button];
      if (!zone) return;
      // some browsers fire back/forward navigation on mouseup; prevent it over the area too
      if (inArea(e.target) && !(e.button === 0 && (e.target as Element)?.closest?.(".kc"))) {
        e.preventDefault();
      }
      // release is otherwise never gated (a mouseup off the area must still clear the depress)
      setHeld((prev) => {
        if (!prev.has(zone)) return prev;
        const next = new Set(prev);
        next.delete(zone);
        return next;
      });
    };
    const ctx = (e: MouseEvent) => {
      if (inArea(e.target)) e.preventDefault(); // no right-click menu over the render area
    };
    const aux = (e: MouseEvent) => {
      if (inArea(e.target) && BUTTON_ZONE[e.button]) e.preventDefault(); // no back/forward/autoscroll
    };
    const wheel = (e: WheelEvent) => {
      // axis-locked horizontal pans emit deltaY 0; ignore them and do not consume the gate
      if (!enabledRef.current || !inArea(e.target) || e.deltaY === 0) return;
      const now = Date.now();
      if (now - wheelGate.current < WHEEL_GATE_MS) return;
      wheelGate.current = now;
      const zone = e.deltaY < 0 ? "WheelUp" : "WheelDown";
      flashRef.current([zone]); // a tick has no natural release: a momentary flash, not a held press
      pressRef.current(zone);
    };
    const blur = () => setHeld((prev) => (prev.size ? new Set<string>() : prev));
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    window.addEventListener("contextmenu", ctx);
    window.addEventListener("auxclick", aux);
    window.addEventListener("wheel", wheel);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("contextmenu", ctx);
      window.removeEventListener("auxclick", aux);
      window.removeEventListener("wheel", wheel);
      window.removeEventListener("blur", blur);
    };
  }, []);

  return held;
}
